import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { RestartAlt, Info, Search, HelpOutline } from '@mui/icons-material';
import axios from 'axios';
import { settingsService, TenantSettingsResponse } from '@services/settings.service';
import { usePermissions } from '@hooks/usePermissions';
import { useTranslation } from 'react-i18next';

interface MetadataInfo {
  document_count: number;
  default_available: boolean;
  metadata_file: string;
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [metadataInfo, setMetadataInfo] = useState<MetadataInfo | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Tenant settings state
  const [tenantSettings, setTenantSettings] = useState<TenantSettingsResponse | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);

  // Permissions
  const { canManageSettings } = usePermissions();

  const fetchMetadataInfo = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await axios.get<MetadataInfo>(
        `${API_BASE_URL}/api/v1/admin/metadata/info`
      );
      setMetadataInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch metadata info:', error);
    }
  }, []);

  const fetchTenantSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const settings = await settingsService.getTenantSettings();
      setTenantSettings(settings);
    } catch (error: any) {
      console.error('Failed to fetch tenant settings:', error);
      setSettingsError(error.message || t('settings.tenantSettingsFetchFailed'));
    } finally {
      setSettingsLoading(false);
    }
  }, [t]);

  // Fetch metadata info and tenant settings on component mount
  useEffect(() => {
    fetchMetadataInfo();
    fetchTenantSettings();
  }, [fetchMetadataInfo, fetchTenantSettings]);

  const handleKeywordMatchingToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setSettingsLoading(true);
    setSettingsError(null);
    setSettingsSuccess(null);

    try {
      const updatedSettings = await settingsService.updateSearchSettings({
        keyword_matching_enabled: newValue,
      });
      setTenantSettings(updatedSettings);
      setSettingsSuccess(
        newValue
          ? t('settings.keywordMatchingEnabled')
          : t('settings.keywordMatchingDisabled')
      );
      // Clear success message after 3 seconds
      setTimeout(() => setSettingsSuccess(null), 3000);
    } catch (error: any) {
      console.error('Failed to update keyword matching setting:', error);
      setSettingsError(error.message || t('settings.updateFailed'));
      // Revert UI state by re-fetching
      await fetchTenantSettings();
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleResetClick = () => {
    setConfirmDialogOpen(true);
    setResultMessage(null);
  };

  const handleConfirmReset = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    setResultMessage(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/admin/metadata/reset`
      );

      if (response.data.success) {
        setResultMessage({
          type: 'success',
          text: t('settings.resetComplete', { count: response.data.document_count }),
        });
        // Refresh metadata info
        await fetchMetadataInfo();
      } else {
        setResultMessage({
          type: 'error',
          text: t('settings.resetFailed'),
        });
      }
    } catch (error: any) {
      console.error('Reset error:', error);
      setResultMessage({
        type: 'error',
        text: t('settings.resetError', { message: error.response?.data?.detail || error.message }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReset = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('settings.systemTitle')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('settings.systemDescription')}
      </Typography>

      {/* Search Settings Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Search sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6">{t('settings.searchSettings')}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Loading State */}
          {settingsLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Error Message */}
          {settingsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {settingsError}
            </Alert>
          )}

          {/* Success Message */}
          {settingsSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {settingsSuccess}
            </Alert>
          )}

          {/* Keyword Matching Toggle */}
          {tenantSettings && !settingsLoading && (
            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tenantSettings.settings.search.keyword_matching_enabled}
                      onChange={handleKeywordMatchingToggle}
                      disabled={!canManageSettings || settingsLoading}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1">
                        {t('settings.keywordMatching')}
                      </Typography>
                      <Tooltip
                        title={t('settings.keywordMatchingTooltip')}
                        arrow
                      >
                        <HelpOutline
                          sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {tenantSettings.settings.search.keyword_matching_enabled
                  ? t('settings.keywordMatchingEnabledDescription')
                  : t('settings.keywordMatchingDisabledDescription')}
              </Typography>

              {!canManageSettings && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('settings.adminRequired')}
                </Alert>
              )}
            </Box>
          )}

          {/* Info about search modes */}
          <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('settings.searchModesTitle')}</strong><br />
              <strong>{t('settings.keywordMatchOnLabel')}:</strong> {t('settings.keywordMatchOnDescription')}<br />
              <strong>{t('settings.keywordMatchOffLabel')}:</strong> {t('settings.keywordMatchOffDescription')}
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Data Management Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <RestartAlt sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6">{t('settings.dataManagement')}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Current Status */}
          {metadataInfo && (
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('settings.currentStatus')}
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  {t('settings.registeredDocuments', { count: metadataInfo.document_count })}
                </Typography>
                <Typography variant="body2">
                  {t('settings.defaultBackup', {
                    status: metadataInfo.default_available
                      ? t('settings.available')
                      : t('settings.unavailable'),
                  })}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Info Alert */}
          <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('settings.dataResetTitle')}</strong><br />
              {t('settings.dataResetDescription')}
            </Typography>
          </Alert>

          {/* Warning Alert */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('settings.warningLabel')}:</strong> {t('settings.dataResetWarning')}
            </Typography>
          </Alert>

          {/* Result Message */}
          {resultMessage && (
            <Alert severity={resultMessage.type} sx={{ mb: 2 }}>
              {resultMessage.text}
            </Alert>
          )}

          {/* Reset Button */}
          <Button
            variant="contained"
            color="warning"
            startIcon={loading ? <CircularProgress size={20} /> : <RestartAlt />}
            onClick={handleResetClick}
            disabled={loading || !metadataInfo?.default_available}
            fullWidth
            sx={{ mt: 2 }}
          >
            {loading ? t('settings.resetting') : t('settings.resetToDefault')}
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelReset}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {t('settings.resetConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {t('settings.resetConfirmQuestion')}
            <br /><br />
            {t('settings.resetConfirmDescription')}
            <br /><br />
            <strong>{t('settings.irreversible')}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmReset} color="warning" variant="contained" autoFocus>
            {t('settings.executeReset')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
